import { _kebabCase } from '@naturalcycles/js-lib'
import { Fade } from '@src/components/fade/Fade.component'
import { Slide, SlideFromType } from '@src/components/slide/Slide.component'
import {
  closePopup,
  openPopup,
  PopupStatuses,
  selectPopupStatus,
} from '@src/store/popup/popup.slice'
import classNames from 'classnames'
import type { HTMLProps, ReactNode, RefObject } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAppDispatch } from './useAppDispatch'
import { useAppSelector } from './useAppSelector'
import styles from './usePopup.module.scss'

/**
 * A hook that manages the state of a popup.
 */
export interface UsePopupProps {
  /**
   * The name of the popup. This is used to identify the popup in the store,
   * and is used in kebab-case as the ID for the popup.
   */
  name: string
  /**
   * A callback that is called when the popup is dismissed.
   */
  onDismiss?: () => void
  /**
   * Additional classes to apply to the popup.
   */
  className?: classNames.ArgumentArray
  /**
   * The content of the popup.
   */
  children: ReactNode
}

export interface UsePopupReturn {
  status: PopupStatuses
  /**
   * Opens the popup.
   */
  open: () => void
  /**
   * Closes the popup.
   */
  close: () => void
  /**
   * A React dom portal that renders the popup in the main element.
   */
  portal: ReactNode
  /**
   * A ref to the overlay element if you should need it.
   */
  overlay: RefObject<HTMLDivElement>
}

/**
 * A hook that manages the state of a popup. It handles rendering the popup
 * as well as opening and closing it. A caller can supply the content of the
 * popup and the hook will manage the rest.
 */
export function usePopup({ name, onDismiss, children, className }: UsePopupProps): UsePopupReturn {
  const dispatch = useAppDispatch()
  const status = useAppSelector(selectPopupStatus)

  const open = useCallback(() => {
    dispatch(openPopup(name))
  }, [])

  const close = useCallback(() => {
    dispatch(closePopup())
    onDismiss?.()
  }, [])

  const main = typeof window !== 'undefined' ? document.querySelector('main') : null

  const [popup, setPopup] = useState<React.ReactNode>(null)
  const [portal, setPortal] = useState<React.ReactNode>(null)
  const overlay = useRef<HTMLDivElement>(null)

  const overlayProps: HTMLProps<HTMLDivElement> = useMemo(
    () => ({
      className: classNames(styles.Popup__overlay, {
        [styles.Popup__closed]:
          status === PopupStatuses.Closed || status === PopupStatuses.Disabled,
      }),
      onClick: close,
    }),
    [status],
  )

  const popupProps: HTMLProps<HTMLDivElement> = useMemo(
    () => ({
      uid: 'popup',
      className: classNames(
        styles.Popup,
        {
          [styles.Popup__closed]:
            status === PopupStatuses.Closed || status === PopupStatuses.Disabled,
          role: 'dialog',
          'aria-modal': 'true',
        },
        className,
      ),
      id: _kebabCase(name),

      // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role
      role: 'dialog',

      // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role#Labeling
      // Using aria-description here isn't feasible because our modal descriptions
      // contain HTML from the CMS. So it's better to render them out to the DOM
      // and refer to them using describedby instead.
      'aria-labelledby': 'popup-title',
      'aria-describedby': 'popup-description',

      // https://www.w3.org/TR/wai-aria-1.1/#aria-modal
      'aria-modal': true,
    }),
    [className, status],
  )

  useEffect(() => {
    setPopup(
      <>
        <Slide
          duration={256}
          from={SlideFromType.Bottom}
          when={status === PopupStatuses.Open}
          {...popupProps}
        >
          <div className={styles.Popup__layout}>
            <div className={styles.Popup__payload} />
            {children}
          </div>
        </Slide>
        <Fade duration={256} when={status === PopupStatuses.Open} {...overlayProps} />
        <div className={classNames(styles.PopupOverlay)} ref={overlay} uid="popup-overlay" />
      </>,
    )
  }, [children, className, status])

  const onKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close()
    }
  }, [])

  useEffect(() => {
    if (status === PopupStatuses.Open) {
      document.addEventListener('keydown', onKeyDown)
    } else if (status === PopupStatuses.Closing) {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [status])

  /*
   * A portal is an element that renders its children in a different part of the
   * DOM. This is useful for rendering modals, popups, and other elements that
   * need to be rendered outside of the normal flow of the page.
   *
   * Here we create a portal that renders the popup in the main element,
   * allowing a little bit more finesse in scroll detection and other
   * interactions.
   */
  useEffect(() => {
    if (!main) {
      return
    }

    const portal = createPortal(popup, main, name)
    setPortal(portal)
  }, [main, popup])

  return {
    status,
    open,
    close,
    portal,
    overlay,
  }
}
